<!--//https://codepen.io/havardob/pen/ExVaELV-->
<template>
  <div class="mitrex-calculator">
    <nav id="navigation" class="mitrex-calculator__navbar noprint" role="navigation">
      <div class="mitrex-logo__wrapper">
        <img 
          class="mitrex-logo"
          src="@/assets/mitrex-logo-no-tagline.png"/>
        <div class="mitrex-logo__text">
          CALCULATOR
        </div>
      </div>
    
      <div 
        v-if="building.building_type"
        class="selection-tracker noprint">
        <div class="flex flex--row flex--align-center"> 

          <div class="selection-section" 
            :class="{ 'selected': active == 'building-type' }"
            @click="active = 'building-type'">
            <div class="section-label">BUILDING TYPE</div>
            <a class="building-type">
              {{ projectBuildingType.label }}
            </a>
          </div>

          <div 
            class="section-divider">
            <span class="fas fa-chevron-right"></span>
          </div>

          <template v-if="building.building_type && projectBuildingType.subtypes">
            <div class="selection-section" 
              :class="{'selected': active == 'building-subtype'}"
              @click="active = 'building-subtype'">
              <div class="section-label">BUILDING SUBTYPE</div>
              <a v-if="building.building_subtype" class="building-type">
                {{ projectBuilding.label }}
              </a>
              <div class="incomplete" v-else>
                <a >
                  Select Building
                </a>
              </div>
            </div>

            <div 
              class="section-divider">
              <span class="fas fa-chevron-right"></span>
            </div>
          </template>
          
          <div 
            class="selection-section"
            :class="{ 'selected': active == 'products' }">

            <v-fade-transition mode="out-in">
              <div v-if="hasProducts" class="flex flex--row flex--align-center">
                <div 
                  @click="scrollTo('#cladding-products'); active = 'products'"
                  v-if="building.products.cladding">
                  <div class="product-sample">
                    <img 
                      :src="projectCladding.image"/>
                  </div>
                </div>
                <div
                  @click="scrollTo('#railing-products'); active = 'products'" 
                  v-if="building.products.railing">
                  <div class="product-sample">
                    <img 
                      :src="projectRailing.image"/>
                  </div>
                </div>
                <div
                  @click="scrollTo('#window-products'); active = 'products'"  
                  v-if="building.products.window">
                  <div class="product-sample">
                    <img 
                      :src="projectWindow.image"/>
                  </div>
                </div>
              </div>

              <div class="incomplete" v-else>
                <div @click="scrollTo('#products'); active = 'products'">
                  <div class="section-label">PRODUCTS</div>
                  <a>
                    Select Products
                  </a>
                </div>
              </div>
            </v-fade-transition>
            
          </div>  
          
          <div class="section-divider">
            <span class="fas fa-chevron-right"></span>
          </div>

          <div 
            class="selection-section" 
            :class="{ 'selected': active == 'building-size' }">
            <v-fade-transition mode="out-in">
              <div 
                @click="scrollTo('#building-size'); 
                  active = 'building-size'; 
                  adjustingBuildingSize = true"
                class="flex flex--row flex--align-center section__building-size"
                v-if="hasProducts">
                <div class="project-size" v-if="building.products.cladding">
                  <div class="section-label">CLADDING</div>
                  <a class="project-size__number">
                    {{ projectSize.cladding | commasep(0) }} SQFT
                  </a>
                </div>
                <div class="project-size" v-if="building.products.railing">
                  <div class="section-label">RAILING</div>
                  <a class="project-size__number">
                    {{ projectSize.railing | commasep(0)}} LF
                  </a>
                </div>
                <div class="project-size" v-if="building.products.window">
                  <div class="section-label">WINDOW</div>
                  <a class="project-size__number">
                    {{ projectSize.window | commasep(0)}} SQFT
                  </a>
                </div>
              </div>
              <div 
                class="incomplete" 
                v-else 
                @click="scrollTo('#products'); active = 'products'">
                <div class="project-size">
                  <div class="section-label">BUILDING SIZE</div>
                  <a class="project-size__number">
                    Select Products
                  </a>
                </div>
              </div>
            </v-fade-transition>
          </div>

          <div class="section-divider">
            <span class="fas fa-chevron-right"></span>
          </div>

          <div
            class="selection-section" 
            :class="{ 'selected': active == 'location' }"
            @click="scrollTo('#location'); active = 'location'">
            <v-fade-transition mode="out-in">
              <div v-if="building.location">
                <div class="section-label">BUILDING LOCATION</div>
                <a>
                  {{ sunlightHours.location }}
                </a>
              </div>
              <div v-else class="incomplete">
                <div class="section-label">BUILDING LOCATION</div>
                <a>
                  Select Building Location
                </a>
              </div>
            </v-fade-transition>
          </div>
        </div>

        <v-fade-transition mode="out-in">
          <div class="buttons report-buttons">
            <button 
              @click="prev('report')"
              v-if="hasProducts && building.location && active == 'report'" 
              class="button is-rounded edit-button mr-2">
              <span>
                Edit Building
              </span>
            </button>
            <button 
              v-if="hasProducts && building.location" 
              class="button is-rounded is-orange"
              @click="active == 'report' ? savePdfReport() : active = 'report'">
              <span v-if="active != 'report'">
                View Web Report
              </span>
              <span v-else>
                Save PDF Report
              </span>
            </button>
          </div>
        </v-fade-transition>

      </div>  
    </nav>

    <div class="slide full-page" v-show="active == 'building-type'">
      <div class="slide__action">
        Step 1. Choose Your Building Type
      </div>
      <div class="slide__content flex flex--row flex--wrap flex--justify-center">
        <div 
          v-for="(buildingType, buildingKey) in mitrexCalculatorData.building_types"
          :key="buildingKey"
          @click="selectBuilding(buildingKey, buildingType)" 
          class="selectable-card"
          :class="{ 'selected': building.building_type == buildingKey }">
          <img
            class="building-image" 
            :src="buildingType.image"/>
          <div class="card-label">{{ buildingType.label }}</div>
        </div>
      </div>
    </div>

    <div 
      class="slide full-page"
      v-if="building.building_type && projectBuildingType.subtypes" 
      v-show="active == 'building-subtype'">
      <div class="slide__action">
        Step 1. Choose Your Building Type
      </div>
      <div class="slide__content flex flex--row flex--wrap flex--justify-center">
        <div 
          v-for="(buildingSubtype, buildingKey) in projectBuildingType.subtypes"
          :key="buildingKey"
          @click="selectBuilding(buildingKey, buildingSubtype)" 
          class="selectable-card"
          :class="{ 'selected': building.building_subtype == buildingKey }">
          <img
            class="building-image" 
            :src="buildingSubtype.image"/>
          <div class="card-label">{{ buildingSubtype.label }}</div>
        </div>
      </div>
    </div>

    <div 
      id="building-image"
      v-if="projectBuilding" 
      v-show="building.building_type && !['report', 'pdf-report', 'building-type', 'building-subtype'].includes(active)"
      class="columns is-multiline">
      <div 
        class="column is-full-mobile is-full-tablet is-two-thirds-desktop
        is-three-quarters-widescreen building__image">
        <img :src="projectBuilding.image"/>
      </div>

      <div class="column is-full-mobile is-full-tablet is-one-third-desktop
        is-one-quarter-desktop options-menu">
        <h1 class="building__header" 
          v-if="building.building_type"
          @click="active = 'building-type'">
          {{ projectBuilding.label }}
        </h1>

        <section id="products" class="menu-section">
          <div class="slide__action">
            Building Products
          </div>
          <div class="help mb-5">
            Click on product categories to see lists of products, and click on 
            products to select them for your building
          </div>

          <v-accordion 
            v-model="accordions.cladding"
            v-if="building.building_size.cladding" 
            has-divider 
            has-top-border>
            <div slot="header" id="cladding-products" class="product__type">
              Solar Cladding
            </div>
            <div slot="content">
              <div 
                v-for="(claddingType, claddingTypeKey) in solarCladdingData"
                :key="claddingTypeKey">
                <div class="product__subtype">{{ claddingTypeKey }}</div>
                <div class="flex flex--row flex--wrap flex--justify-center">
                  <div
                    v-for="(cladding, claddingKey) in claddingType" 
                    :key="claddingKey"
                    @click="building.products.cladding == claddingKey ? 
                      building.products.cladding = null :
                      building.products.cladding = claddingKey"
                    class="selectable-card"
                    :class="{ 'selected': building.products.cladding == claddingKey }">
                    <img
                      class="product__image" 
                      :src="cladding.image" 
                      :alt="cladding.sample_name"/>
                    <div class="card-label">
                      <div class="product__name">{{ cladding.sample_name }}</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </v-accordion>

          <v-accordion
            v-model="accordions.railing" 
            has-divider 
            has-top-border
            v-if="building.building_size.railing">
            <div slot="header" id="railing-products" class="product__type">
              Solar Railing
            </div>
            <div 
              slot="content" 
              class="flex flex--row flex--wrap flex--justify-center py-4">
              <div
                v-for="(railing, railingKey) in mitrexCalculatorData.products.railing" 
                :key="railingKey"
                @click="building.products.railing == railingKey ?
                  building.products.railing = null :
                  building.products.railing = railingKey"
                class="selectable-card"
                :class="{ 'selected': building.products.railing == railingKey }">
                <img 
                  class="product__image"
                  :src="railing.image" 
                  :alt="railing.sample_name"/>
                <div class="card-label">
                  <div class="product__name">{{ railing.sample_name }}</div>
                </div>
              </div>
            </div>
          </v-accordion>

          <v-accordion 
            v-model="accordions.window" 
            has-divider 
            has-top-border
            v-if="building.building_size.window">
            <div slot="header" id="window-products" class="product__type">
              Solar Window
            </div>
            <div 
              slot="content" 
              class="flex flex--row flex--wrap flex--justify-center py-4">
              <div
                v-for="(window, windowKey) in mitrexCalculatorData.products.window" 
                :key="windowKey"
                @click="building.products.window == windowKey ?
                  building.products.window = null :
                  building.products.window = windowKey"
                class="selectable-card"
                :class="{ 'selected': building.products.window == windowKey }">
                <img
                  class="product__image" 
                  :src="window.image" 
                  :alt="window.sample_name"/>
                <div class="card-label">
                  <div class="product__name">{{ window.sample_name }}</div>
                </div>
              </div>
            </div>
          </v-accordion>

          <!-- <hr v-if="building.building_size.cladding && 
            (building.building_size.railing || building.building_size.window)"/> -->
<!-- 
          <hr v-if="building.building_size.railing"/> -->

        </section>

        <section 
          id="building-size" 
          class="menu-section">

          <div class="slide__action">
            Building Size
          </div>
          <div class="help mb-3">
            Click anywhere on the table to adjust the size of the building
          </div>

          <article v-if="!hasProducts" class="empty-message">
            Please select some products first
          </article>
          
          <table 
            v-else
            @click="adjustingBuildingSize = true" 
            class="table responsive__table">
            <tbody>
              <tr v-if="building.products.cladding">
                <td data-title="Product Type">
                  <div class="product-type">
                    SOLAR CLADDING
                  </div>
                  <div class="flex flex--row flex--align-center">
                    <div class="product-sample">
                      <img :src="projectCladding.image"/>
                    </div>
                    <div>
                      {{ projectCladding.sample_name }}
                    </div>
                  </div>
                </td>
                <td
                  :class="{ 'default': !adjustedBuildingSize }" 
                  data-title="Total Measure">
                  <a>
                    {{ projectSize.cladding | commasep(0) }} SQFT
                  </a>
                </td>
              </tr>

              <tr v-if="building.products.railing">
                <td data-title="Product Type">
                  <div class="product-type">
                    SOLAR RAILING
                  </div>
                  <div class="flex flex--row flex--align-center">
                    <div class="product-sample">
                      <img :src="projectRailing.image"/>
                    </div>
                    <div>
                      {{ projectRailing.sample_name }}
                    </div>
                  </div>
                </td>
                <td
                  :class="{ 'default': !adjustedBuildingSize }" 
                  data-title="Total Measure">
                  <a>
                    {{ projectSize.railing | commasep(0) }} LF
                  </a>
                </td>
              </tr>

              <tr v-if="building.products.window">
                <td data-title="Product Type">
                  <div class="product-type">
                    SOLAR WINDOW
                  </div>
                  <div class="flex flex--row flex--align-center">
                    <div class="product-sample">
                      <img :src="projectWindow.image"/>
                    </div>
                    <div>
                      {{ projectWindow.sample_name }}
                    </div>
                  </div>
                </td>
                <td 
                  :class="{ 'default': !adjustedBuildingSize }" 
                  data-title="Total Measure">
                  <a>
                    {{ projectSize.window | commasep(0) }} SQFT
                  </a>
                </td>
              </tr>
            </tbody>
          </table>
        </section>

        <section id="location" class="menu-section">

          <div class="slide__action">
            Building Location
          </div>

          <div class="slide__content">
            <div class="select is-rounded is-large">
              <select 
                :value="building.location"
                @change="building.location = $event.target.value">
                <option 
                  v-for="(country, countryKey) in mitrexCalculatorData.sunlight_hours"
                  :key="countryKey"
                  :value="countryKey">
                  {{ country.location }}
                </option>
              </select>
            </div>
          </div>

        </section>

      </div>
    </div>

    <div 
      class="report" 
      v-if="hasProducts && building.location"
      v-show="active == 'report'">
      <div class="slide__content container">
        <section v-if="active == 'report'" class="report__section">
          <div class="report__header">Project Details</div>

          <div>
            Location: {{ sunlightHours.location }}
          </div>

          <table class="table responsive__table is-fullwidth has-text-left mt-3">
            <thead>
              <tr class="v-centered">
                <th rowspan="2">Type of Product</th>
                <th rowspan="2">
                  Total Size <div class="unit-label">SQFT / LF</div>
                </th>
                <th rowspan="2" class="border--after">
                  System Size <div class="unit-label">kW</div>
                </th>
                <th colspan="2" class="has-text-centered border--after">
                  MPA
                </th>
                <th colspan="2" class="has-text-centered">DPA</th>
              </tr>
              <tr>
                <th class="text--nowrap has-text-centered">
                  Cost <span class="unit-label">$</span>
                </th>
                <th class="text--nowrap has-text-centered border--after">
                  Energy Revenue Over 30 Yrs <span class="unit-label">$</span>
                </th>
                <th class="text--nowrap has-text-centered">
                  Cost <span class="unit-label">$</span>
                </th>
                <th class="text--nowrap has-text-centered border--after">
                  Energy Revenue Over 30 Yrs <span class="unit-label">$</span>
                </th>
              </tr>
            </thead>
            
            <tbody>
              <template 
                v-for="productType in ['cladding', 'railing', 'window']">
                <tr 
                  :key="productType"
                  v-if="building.products[productType]">
                  <td data-title="Product Type">
                    Solar <span class="text--capitalize">{{ productType }}</span>
                  </td>
                  <td data-title="Total Size (SQFT/LF)">
                    <v-animated-number 
                      :value="projectSize[productType]"
                      :format="v => $options.filters.commasep(v,0)"/>
                  </td>
                  <td data-title="System Size (kW)" class="border--after">
                    <v-animated-number 
                      :value="projectSummary[productType].system_size"
                      :format="v => $options.filters.commasep(v,0)"/>
                  </td>
                  <td data-title="MPA Cost ($)" class="has-text-centered">
                    <v-animated-number 
                      :value="projectSummary[productType].total_cost_mpa"
                      :format="v => $options.filters.commasep(v,0)"/>
                  </td>
                  <td data-title="MPA 30-yr Energy Revenue ($)" 
                    class="has-text-centered border--after">
                    <v-animated-number 
                      :value="projectSummary[productType].energy_revenue_mpa * 30"
                      :format="v => $options.filters.commasep(v,0)"/>
                  </td>
                  <td data-title="DPA Cost ($)" class="has-text-centered">
                    <v-animated-number 
                      :value="projectSummary[productType].total_cost_dpa"
                      :format="v => $options.filters.commasep(v,0)"/>
                  </td>
                  <td data-title="DPA 30-yr Energy Revenue ($)" 
                    class="has-text-centered border--after">
                    <v-animated-number 
                      :value="projectSummary[productType].energy_revenue_dpa * 30"
                      :format="v => $options.filters.commasep(v,0)"/>
                  </td>
                </tr>
              </template>
            </tbody>

            <tfoot v-if="(!!building.products.cladding + !!building.products.railing + !!building.products.window) > 1">
              <tr>
                <td class="hide-smallscreen" colspan="2">
                </td>
                <td 
                  data-title="Total System Size (kW)" 
                  class="border--after">
                  {{ Object.values(projectSummary)
                      .map(d => d.system_size)
                      .reduce((a,b) => a + b, 0) | commasep(0) }}
                </td>
                <td
                  data-title="Total MPA Cost" 
                  class="has-text-centered">
                  {{ Object.values(projectSummary)
                      .map(d => d.total_cost_mpa)
                      .reduce((a,b) => a + b, 0) | commasep(0) }}
                </td>
                <td
                  data-title="Total MPA 30-yr Energy Revenue ($)"  
                  class="has-text-centered border--after">
                  {{ Object.values(projectSummary)
                      .map(d => d.energy_revenue_mpa)
                      .reduce((a,b) => a + b, 0) * 30 | commasep(0) }}
                </td>
                <td
                  data-title="Total DPA Cost"  
                  class="has-text-centered">
                  {{ Object.values(projectSummary)
                      .map(d => d.total_cost_dpa)
                      .reduce((a,b) => a + b, 0) | commasep(0) }}
                </td>
                <td 
                  data-title="Total DPA 30-yr Energy Revenue ($)" 
                  class="has-text-centered border--after">
                  {{ Object.values(projectSummary)
                      .map(d => d.energy_revenue_dpa)
                      .reduce((a,b) => a + b, 0) * 30 | commasep(0) }}
                </td>
              </tr>
            </tfoot>
          </table>
        </section>

        <section class="report__section">
          <div class="report__header">Products</div>
          <div class="flex flex--row flex--wrap flex--align-center flex--justify-center">
            <div class="report__product" v-if="building.products.cladding">
              <img
                class="product__image--cladding" 
                :src="projectCladding.image" 
                :alt="projectCladding.sample_name"/>
              <div class="card-label">
                <div class="product__type--bolded">
                  SOLAR CLADDING
                </div>
                <div class="product__name">{{ projectCladding.sample_name }}</div>
              </div>
            </div>
            <div class="report__product" v-if="building.products.railing">
              <img
                class="product__image" 
                :src="projectRailing.image" 
                :alt="projectRailing.sample_name"/>
              <div class="card-label">
                <div class="product__type--bolded">
                  SOLAR RAILING
                </div>
                <div class="product__name">{{ projectRailing.sample_name }}</div>
              </div>
            </div>
            <div class="report__product" v-if="building.products.window">
              <img
                class="product__image" 
                :src="projectWindow.image" 
                :alt="projectWindow.sample_name"/>
              <div class="card-label">
                <div class="product__type--bolded">
                  SOLAR WINDOW
                </div>
                <div class="product__name">{{ projectWindow.sample_name }}</div>
              </div>
            </div>
          </div>
          
        </section>
        
        <section 
          v-if="active == 'report'" 
          class="report__section flex flex--column flex--align-center">
          <div class="report__header">Environmental Impact</div>
          <div class="report__subheader">Environmental Impact Over 30 Years</div>

          <div class="env-impact__graphics">
            <div class="env-impact__wrapper">
              <div class="env-impact__first">
              </div>
              <div class="env-impact__second">
                <div class="env-impact__item">
                  Reducing 
                  <div class="env-impact__number">
                    {{ Object.values(projectSummary)
                        .map(d => d.metric_tonnes_co2_per_year)
                        .reduce((a,b) => a + b, 0) * 30 | commasep(0) }} tonnes
                  </div>
                  of CO2
                </div>
                <div class="env-impact__spacer">
                </div>
                <div class="env-impact__item">
                  Planting 
                  <div class="env-impact__number">
                    {{ Object.values(projectSummary)
                      .map(d => d.trees_planted_per_year)
                      .reduce((a,b) => a + b, 0) * 30 | commasep(0) }}
                  </div>
                  Trees
                </div>
              </div>
            </div>
            <img 
              src="https://camas.app/resources/static/images/images/image__e6d39c514ee953501fa4fe102daebbb9.jpg"
              alt="environmental impact"/>
            <div class="env-impact__wrapper">
              <div class="env-impact__first">
                <div class="env-impact__item">
                  <div class="env-impact__number">
                    {{ Object.values(projectSummary)
                      .map(d => d.energy_per_year)
                      .reduce((a,b) => a + b, 0) * 30 | commasep(0) }} kWh
                  </div>
                  Green Energy Produced
                </div>
              </div>
              
              <div class="env-impact__second">
                <div class="env-impact__spacer large">
                </div>
                <div class="env-impact__item">
                  Powering 
                  <div class="env-impact__number">
                    {{ Object.values(projectSummary)
                      .map(d => d.electricity_homes_per_year)
                      .reduce((a,b) => a + b, 0) * 30 | commasep(0) }}
                  </div>
                  Homes
                </div>
                <div class="env-impact__spacer">
                </div>
                <div class="env-impact__item">
                  Removing 
                  <div class="env-impact__number">
                    {{ Object.values(projectSummary)
                      .map(d => d.cars_off_road_per_year)
                      .reduce((a,b) => a + b, 0) * 30 | commasep(0) }}
                  </div>
                  Cars
                </div>
              </div>
            </div>
            <div class="env-impact__footnote">
              *Source: Greenhouse Gas Equivalencies Calculator, US EPA
            </div>
          </div>
        </section>

        <div class="report__section">
          <template v-if="taxIncentives.length > 0">
            <div class="report__header">Tax Incentives</div>
            <div v-for="(taxIncentive, i) in taxIncentives" :key="i">
              <a :href="taxIncentive.link" target="_blank">
                {{ taxIncentive.name }}
              </a>
            </div>
          </template>
        </div>
        
      </div>
    </div>

    <div class="pdf-report-container">
      <mitrex-calculator-report
        v-if="hasProducts && building.location && active == 'pdf-report'"
        :sunlight-hours="sunlightHours"
        :project-size="projectSize"
        :project-building="projectBuilding"
        :project-summary="projectSummary"
        :project-cladding="projectCladding"
        :project-railing="projectRailing"
        :project-window="projectWindow"
        :tax-incentives="taxIncentives"
        @rendered="print()"/>
    </div>

    <div
      class="modal"
      :class="{ 'is-active': adjustingBuildingSize }">

      <div class="modal-background"
        @click="adjustingBuildingSize = false">
      </div>

      <div class="modal-content">
        <div class="modal-card">

          <header class="modal-card-head">
            <p class="modal-card-title">Adjust Building Size</p>
            <button
              class="delete"
              aria-label="close"
              @click="adjustingBuildingSize = false">
            </button>
          </header>

          <div class="modal-card-body">
            <div class="building-size__instructions">
              Click into any input to enter custom building size numbers.
            </div>
            <table 
              class="table responsive__table building-size__table"
              :class="{ 'default': !adjustedBuildingSize }">
              <thead>
                <tr>  
                  <th>
                    Product
                  </th>
                  <th>
                    North Size <div class="unit-label">SQFT / LF</div>
                  </th>
                  <th>
                    South Size <div class="unit-label">SQFT / LF</div>
                  </th>
                  <th>
                    East Size <div class="unit-label">SQFT / LF</div>
                  </th>
                  <th>
                    West Size <div class="unit-label">SQFT / LF</div>
                  </th>
                  <th class="text--nowrap">
                    Project Size <div class="unit-label">SQFT / LF</div>
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr v-if="building.products.cladding">
                  <td data-title="Product">
                    <div>
                      <div class="product-type">
                        SOLAR CLADDING
                      </div>
                      <div class="flex flex--row flex--align-center">
                        <div class="product-sample">
                          <img :src="projectCladding.image"/>
                        </div>
                        <div>
                          {{ projectCladding.sample_name }}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td 
                    v-for="(elevationValue, elevation) in building.building_size.cladding"
                    :data-title="`${$options.filters.titlecase(elevation)} Size (SQFT)`"
                    :key="elevation">
                    <input 
                      class="input is-medium is-rounded" 
                      type="number" 
                      :placeholder="`${elevation} elevation SQFT`"
                      v-model="building.building_size.cladding[elevation]"
                      @input="adjustedBuildingSize = true"/>
                  </td>
                  <td data-title="Total Project Cladding">
                    <div class="project-size">
                      {{ projectSize.cladding | commasep(0) }} SQFT
                    </div>
                  </td>
                </tr>

                <tr 
                  v-if="building.products.cladding && building.products.railing" 
                  class="mobile-spacer">
                </tr>

                <tr v-if="building.products.railing">
                  <td data-title="Product">
                    <div>
                      <div class="product-type">
                        SOLAR RAILING
                      </div>
                      <div class="flex flex--row flex--align-center">
                        <div class="product-sample">
                          <img :src="projectRailing.image"/>
                        </div>
                        <div>
                          {{ projectRailing.sample_name }}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td 
                    v-for="(elevationValue, elevation) in building.building_size.railing"
                    :data-title="`${$options.filters.titlecase(elevation)} Size (LF)`"
                    :key="elevation">
                    <input 
                      class="input is-medium is-rounded" 
                      type="number" 
                      :placeholder="`${elevation} elevation LF`"
                      v-model="building.building_size.railing[elevation]"
                      @input="adjustedBuildingSize = true"/>
                  </td>
                  <td data-title="Total Project Railing">
                    <div class="project-size">
                      {{ projectSize.railing | commasep(0) }} LF
                    </div>
                  </td>
                </tr>

                <tr 
                  v-if="building.products.railing && building.products.window" 
                  class="mobile-spacer">
                </tr>

                <tr v-if="building.products.window">
                  <td data-title="Product">
                    <div>
                      <div class="product-type">
                        SOLAR WINDOW
                      </div>
                      <div class="flex flex--row flex--align-center">
                        <div class="product-sample">
                          <img :src="projectWindow.image"/>
                        </div>
                        <div>
                          {{ projectWindow.sample_name }}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td 
                    v-for="(elevationValue, elevation) in building.building_size.window"
                    :data-title="`${$options.filters.titlecase(elevation)} size (SQFT)`"
                    :key="elevation">
                    <input 
                      class="input is-medium is-rounded" 
                      type="number" 
                      :placeholder="`${elevation} elevation SQFT`"
                      v-model="building.building_size.window[elevation]"
                      @input="adjustedBuildingSize = true"/>
                  </td>
                  <td data-title="Total Project Window">
                    <div class="project-size">
                      {{ projectSize.window | commasep(0) }} SQFT
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <footer class="modal-card-foot">
            <button 
              @click="adjustingBuildingSize = false"
              class="button is-primary is-rounded">
              Done
            </button>
          </footer>

        </div>
      </div>
    </div>

    <div v-if="hasProducts && building.location" class="bottom-nav noprint">
      <div class="buttons">
        <button 
          @click="prev('report')"
          v-if="hasProducts && building.location && active == 'report'" 
          class="button is-rounded edit-button mr-2">
          <span>
            Edit
          </span>
        </button>
        <button 
          v-if="hasProducts && building.location" 
          class="button is-rounded is-orange"
          @click="active == 'report' ? savePdfReport() : active = 'report'">
          <span v-if="active != 'report'">
            View Web Report
          </span>
          <span v-else>
            Save PDF Report
          </span>
        </button>
      </div>
    </div>

  </div>
</template>

<script>
import { mitrexCalculatorData } from '@/data/mitrex-calculator'
import VAnimatedNumber from '@/components/base/VAnimatedNumber'
import VAccordion from '@/components/base/VAccordion'
import VFadeTransition from '@/components/transitions/VFadeTransition'
import MitrexCalculatorReport from '@/components/mitrexCalculator/MitrexCalculatorReport'

export default {
  components: { VFadeTransition, VAnimatedNumber, VAccordion, MitrexCalculatorReport },
  data() {
    return {
      active: 'building-type',
      slides: ['building-type', 'building-subtype', 'products', 'building-size', 'location', 'report'],
      building: {
        building_type: null,
        building_subtype: null,
        products: { cladding: null, railing: null, window: null },
        building_size: { 
          cladding: { north: 0, south: 0, east: 0, west: 0 },
          railing: { north: 0, south: 0, east: 0, west: 0 },
          window: { north: 0, south: 0, east: 0, west: 0 }
        },
        location: null
      },
      accordions: {
        cladding: false,
        railing: false,
        window: false
      },
      mitrexCalculatorData: mitrexCalculatorData,
      adjustingBuildingSize: false,
      adjustedBuildingSize: false
    }
  },
  computed: {
    solarCladdingData({ mitrexCalculatorData }) {
      let cladding = mitrexCalculatorData.products.cladding;
      let claddingTypes = [
        ...new Set(Object.values(cladding).map(d => d.sample_type))
      ]

      return Object.fromEntries(
        claddingTypes.map(claddingType => {
          let res = Object.fromEntries(
            Object.entries(cladding).filter(d => {
              return d[1].sample_type == claddingType
            })
          )
          return [claddingType, res]
        })
      )
    },
    hasProducts({ building }) {
      return Object.values(building.products).filter(d => d != null).length > 0
    },
    sunlightHours({ building, mitrexCalculatorData }) {
      return mitrexCalculatorData.sunlight_hours[building.location];
    },
    projectSize({ building }) {
      return Object.fromEntries(
        Object.entries(building.building_size).map(d => {
          return [d[0], Object.values(d[1]).reduce((a,b) => +a + +b, 0)]
        })
      )
    },
    projectBuildingType({ mitrexCalculatorData, building }) {
      return mitrexCalculatorData.building_types[building.building_type]
    }, 
    projectBuilding({ projectBuildingType, building }) {
      if (projectBuildingType) {
        return projectBuildingType.subtypes ? projectBuildingType.subtypes[building.building_subtype] :
          projectBuildingType
      } else {
        return null
      }
    },
    projectCladding({ mitrexCalculatorData, building }) {
      return mitrexCalculatorData.products.cladding[building.products.cladding]
    },
    projectRailing({ mitrexCalculatorData, building }) {
      return mitrexCalculatorData.products.railing[building.products.railing]
    },
    projectWindow({ mitrexCalculatorData, building }) {
      return mitrexCalculatorData.products.window[building.products.window]
    },
    taxIncentives({ mitrexCalculatorData, building }) {
      return mitrexCalculatorData.tax_incentives.filter(d => d.key == building.location);
    },
    projectSummary({ sunlightHours, projectSize, projectCladding, projectRailing, 
      projectWindow, building, mitrexCalculatorData }) {

      const params = mitrexCalculatorData.master_parameters;

      let res = {};

      if (building.products.cladding) {
        let claddingEnergy = building.products.cladding ? ['north', 'south', 'east', 'west'].map(direction => {
          return +building.building_size.cladding[direction] * sunlightHours[`${direction}_sunlight_hours`] * projectCladding.watt_sqft / 1000
        }).reduce((a,b) => a + b, 0) : 0;

        res['cladding'] = {
          total_cost_mpa: projectSize.cladding * projectCladding.mpa_price,
          total_cost_dpa: projectSize.cladding * projectCladding.dpa_price,
          energy_revenue_mpa: claddingEnergy * sunlightHours.customer_share_dollars,
          energy_revenue_dpa: claddingEnergy * sunlightHours.electricity_price_kwh,
          system_size: projectSize.cladding * projectCladding.watt_sqft / 1000,
          energy_per_year: claddingEnergy,
          cars_off_road_per_year: claddingEnergy / params.energy_kwh * params.cars_per_year,
          metric_tonnes_co2_per_year: claddingEnergy / params.energy_kwh * params.metric_tonnes_co2_per_year,
          electricity_homes_per_year: claddingEnergy / params.energy_kwh * params.electricity_homes_per_year,
          trees_planted_per_year: claddingEnergy / params.energy_kwh * params.acres_of_forest_per_year,
        }
      }

      if (building.products.railing) {
        let railingEnergy = building.products.railing ? ['north', 'south', 'east', 'west'].map(direction => {
          return +building.building_size.railing[direction] * sunlightHours[`${direction}_sunlight_hours`] * projectRailing.watt_lft / 1000
        }).reduce((a,b) => a + b, 0) : 0;

        res['railing'] = {
          total_cost_mpa: projectSize.railing * projectRailing.mpa_price,
          total_cost_dpa: projectSize.railing * projectRailing.dpa_price,
          energy_revenue_mpa: railingEnergy * sunlightHours.customer_share_dollars,
          energy_revenue_dpa: railingEnergy * sunlightHours.electricity_price_kwh,
          system_size: projectSize.railing * projectRailing.watt_lft / 1000,
          energy_per_year: railingEnergy,
          cars_off_road_per_year: railingEnergy / params.energy_kwh * params.cars_per_year,
          metric_tonnes_co2_per_year: railingEnergy / params.energy_kwh * params.metric_tonnes_co2_per_year,
          electricity_homes_per_year: railingEnergy / params.energy_kwh * params.electricity_homes_per_year,
          trees_planted_per_year: railingEnergy / params.energy_kwh * params.acres_of_forest_per_year,
        }
      }

      if (building.products.window) {
        let windowEnergy = building.products.window ? ['north', 'south', 'east', 'west'].map(direction => {
          return +building.building_size.window[direction] * sunlightHours[`${direction}_sunlight_hours`] * projectWindow.watt_sqft / 1000
        }).reduce((a,b) => a + b, 0) : 0;

        res['window'] = {
          total_cost_mpa: projectSize.window * projectWindow.mpa_price,
          total_cost_dpa: projectSize.window * projectWindow.dpa_price,
          energy_revenue_mpa: windowEnergy * sunlightHours.customer_share_dollars,
          energy_revenue_dpa: windowEnergy * sunlightHours.electricity_price_kwh,
          system_size: projectSize.window * projectWindow.watt_sqft / 1000,
          energy_per_year: windowEnergy,
          cars_off_road_per_year: windowEnergy / params.energy_kwh * params.cars_per_year,
          metric_tonnes_co2_per_year: windowEnergy / params.energy_kwh * params.metric_tonnes_co2_per_year,
          electricity_homes_per_year: windowEnergy / params.energy_kwh * params.electricity_homes_per_year,
          trees_planted_per_year: windowEnergy / params.energy_kwh * params.acres_of_forest_per_year,
        }
      }
      
      return res
    }
  },
  methods: {
    prev(current) {
      let index = this.slides.indexOf(current);
      this.active = this.slides[index - 1];
    },
    next(current) {
      let index = this.slides.indexOf(current);
      this.active = this.slides[index + 1];
    },
    selectBuilding(buildingKey, buildingType) {
      this.building.products.cladding = null;
      this.building.products.railing = null;
      this.building.products.window = null;
      this.accordions.cladding = false;
      this.accordions.railing = false;
      this.accordions.window = false;
      this.scrollTo("#navigation");

      if (buildingType.subtypes) {
        this.building.building_type = buildingKey;
        this.next('building-type');
      } else {
        this.building.building_size = buildingType.default_size;
        this.next('building-subtype');
        if (buildingType.is_subtype) {
          this.building.building_subtype = buildingKey;
        } else {
          this.building.building_type = buildingKey;
          this.building.building_subtype = null;
        }
      }
    },
    scrollTo(id) {
      const el = document.querySelector(id);

      if (el) {
        el.scrollIntoView({ behavior: 'smooth' });
      }
    },
    savePdfReport() {
      this.active = 'pdf-report';
    },
    print() {
      window.print();
    }
  }
}
</script>

<style scoped lang="scss">
@import '@/styles/flexbox.scss';
@import '@/styles/printable.scss';

$tablet: 769px;
$desktop: 1024px;
$widescreen: 1216px;
$fullhd: 1408px;

.mitrex-calculator {
  font-family: 'Montserrat', sans-serif;
  min-height: 100vh;
  max-width: 100vw;
  display: flex;
  flex-flow: column;
  overflow-x: auto;

  .columns {
    margin-left: 0em;
    margin-right: 0em;
  }
}

.pdf-report-container {
  max-width: 100vw;
  overflow-x: auto;
}

.mitrex-calculator {
  &__navbar {
    display: flex;
    flex-direction: row;
    flex: 0 1 auto;

    .edit-button {
      display: none;
    }
  
    @media (min-width: $desktop) {
      .edit-button {
        display: flex;
      }
    }
  }
}

.mitrex-logo {
  width: 120px;
  height: auto;
  margin-left: 15px;
  margin-right: 15px;
  margin-top: 10px;
  @media (min-width: $desktop) {
    width: 160px;
    height: auto;
    margin-right: 50px;
  }
  &__wrapper {
    min-width: 150px;
  }
  &__text {
    margin-left: 17px;
    margin-bottom: 10px;
    color: #0E4E74;
    font-size: 0.75em;
  }
}

.building {
  &__image {
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: center;
    img {
      width: auto;
      padding-top: 62px;
      height: 80%;
    }

    @media (max-width: $desktop) {
      height: 50vh;
    }

    @media (min-width: $desktop) {
      height: calc(100vh - 62px);
      img {
        padding-bottom: 61px;
      }
    }
  }

  &__header {
    font-size: 2em;
    color: #4a4a4a;
    cursor: pointer;
    &:hover {
      color: #F16635;
    }
    @media (max-width: $desktop) {
      font-size: 1.5em;
    }
  }
}

.options-menu {
  text-align: center;
  padding-top: 2em;
  // -ms-overflow-style: none;
  // scrollbar-width: none;
  // &::-webkit-scrollbar {
  //   display: none;
  // }

  @media (min-width: $desktop) {
    max-height: calc(100vh - 62px);
    overflow-y: auto;
  }

  .product {
    &__subtype {
      margin-top: 1em;
      font-size: 0.9em;
      text-transform: uppercase;
    }
    &__image {
      width: 50px;
      min-height: 50px;
    }
    &__name {
      font-weight: normal;
      font-size: 0.75em;
      color: #bbb;
    }
  }

  .selectable-card {
    padding: 0.25em;
    margin: 0.3em;
    &:hover {
      .product__name {
        color: #F16635;
      }
    }
    &.selected {
      .product__name {
        color: #F16635;
      }
    }
  }

  .menu-section {
    padding: 2em 0em;
  }

  #building-size {
    &:hover {
      a {
        color: #F16635;
      }
    }
    table {
      cursor: pointer;
    }
  }
}

.slide {
  padding: 1em 1em;
  text-align: center;

  &__action {
    font-size: 1.5em;
    color: #bbb;
    font-weight: bold;
    margin-bottom: 0.5em;
  }

  &__content {
    padding-top: 1em;
    padding-left: 1em;
    padding-right: 1em;
    padding-bottom: 61px;
  }

  &.full-page {
    flex: 1 1 auto;
    display: flex;
    flex-flow: column;
    .slide__content {
      display: flex;
      flex: 1 1 auto;
      align-items: center;
      justify-content: center;
    }
  }
}

.selectable-card {
  padding: 0.5em;
  margin: 0.5em;
  border-radius: 6px;
  transition: all 0.2s ease-in-out;
  cursor: pointer;
  overflow: hidden;
  &:hover {
    transform: scale(1.05);
    img {
      opacity: 0.75;
    }
    .card-label {
      color: #F16635;
    }
  }
  &.selected {
    transform: scale(1.1);
    border: 1px solid #F16635;
    box-shadow: 0 8px 8px rgba(10,10,10,0.1);
    &:before {
      content: "";
      position: absolute;
      top: -1px;
      right: -1px;
      width: 0;
      height: 0;
      border-top: 17px solid #F16635;
      border-left: 17px solid transparent;
    }
    .card-label {
      color: #F16635;
    }
  }
}

.building-image {
  width: 280px;
  height: auto;
}

.building-size__instructions {
  margin-bottom: 1em;
}

.building-size__table {
  .mobile-spacer {
    height: 5px;
  }
  .project-size {
    font-size: 1.2em;
    font-weight: bold;
    padding-bottom: 0.5em;
  }
  @media (max-width: $desktop) {
    td:first-child {
      padding-top: 1.2em;
    }
  }
  @media (min-width: $desktop) {
    .project-size {
      font-size: 1.5em;
      padding-bottom: 0em;
      transition: all 300ms ease-in-out;
    }
    .mobile-spacer {
      display: none
    }
  }
  &.default {
    input {
      color: #bbb;
      transition: all 300ms ease-in-out;
    }
    .project-size {
      color: #bbb;
    }
  }
}

.product {
  &__type {
    font-size: 1.2em;
    font-weight: bold;
    margin: 0.5em 0em;
  } 
  &__type--bolded {
    font-size: 0.8em;
    font-weight: bold;
    color: #989898;
    padding-bottom: 0.3em;
  }
  &__subtype {
    margin-top: 1em;
    font-size: 1em;
    text-transform: uppercase;
  }
  &__image {
    width: 200px;
    min-height: 200px;
    height: auto;
  }
  &__name {
    font-weight: bold;
  }
  &__price {
    color: #bbb;
    font-size: 0.8em;
  }
  &__price-label {
    font-weight: bold;
  }
}

.text--nowrap {
  white-space: nowrap
}

.responsive {
  &__table {
    width: 100%;
    @media (min-width: $desktop) {
      td:first-child {
        text-align: left;
        white-space: nowrap;
      }
    }
    .product-type {
      font-size: 0.8em;
      color: #bbb;
      padding-bottom: 0.3em;
    }
    thead {
      // Accessibly hide <thead> on narrow viewports
      position: absolute;
      clip: rect(1px 1px 1px 1px); /* IE6, IE7 */
      padding: 0;
      border: 0;
      height: 1px; 
      width: 1px; 
      overflow: hidden;
      
      @media (min-width: $desktop) {
        // Unhide <thead> on wide viewports
        position: relative;
        clip: auto;
        height: auto;
        width: auto;
        overflow: auto;
      }
    }

    .default {
      a {
        color: #bbb;
      }
    }
    
    // Set these items to display: block for narrow viewports
    tbody,
    tr,
    th,
    td {
      display: block;
      padding: 0;
      text-align: left;
      white-space: normal;
    }

    td.hide {
      display: none;
      @media (min-width: $desktop) {
        display: table-cell;
      }
    }
    
    tr {   
      @media (min-width: $desktop) {
        // Undo display: block 
        display: table-row; 
      }
    }

    td {
      min-height: 41px;
      @media (max-width: $desktop) {
        &:not(:last-child) {
          border-bottom: 1px solid #dbdbdb;
        }
      }
    }
    
    th,
    td {
      padding: .5em;
      vertical-align: middle;
      
      @media (min-width: $desktop) {
        // Undo display: block 
        display: table-cell;
      }
    }

    tbody {
      @media (min-width: $desktop) {
        // Undo display: block 
        display: table-row-group; 
      }
      
      tr {
        @media (max-width: $desktop) {
          &:not(:last-child) {
            border-bottom: 2px solid #bbb;
          }
        }

        @media (min-width: $desktop) {
          // Undo display: block 
          display: table-row;
        }
        
        &:last-of-type {
          margin-bottom: 0; 
        }
      }

      td {
        @media (max-width: $desktop) {
          text-align: right !important;
          .flex {
            justify-content: flex-end;
          }
        }
        
        @media (min-width: $desktop) {
          text-align: left; 
        }
      }
    }

    td[data-title]:before {
      content: attr(data-title);
      float: left;
      font-size: .8em;
      color: rgba(94,93,82,.75);
      
      @media (min-width: $tablet) {
        font-size: .9em; 
      }
      
      @media (min-width: $desktop) {
        // Don’t show data-title labels 
        content: none; 
      }
    } 

    tfoot {
      tr {
        @media (max-width: $desktop) {
          border-top: 0px;
          border-top: 2px solid #bbb;
        }
      }
      td {
        @media (max-width: $desktop) {
          text-align: right !important;
          border-top: 0px;
        }
      }
    }
  }
}

table {
  .unit-label {
    font-weight: normal;
    color: #bbb;
    font-size: 0.8em;
  }
  .product-sample {
    width: 30px;
    height: 30px;
    border-radius: 50%;
    overflow: hidden;
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: center;
    object-fit: contain;
    margin-right: 0.5em;
  }
}

.bottom-nav {
  @media (min-width: $desktop) {
    display: none;
  }

  position: fixed;
  bottom: 0px;
  background: whitesmoke;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  width: 100%;
  z-index: 5;
  padding: 10px 15px;
}

.selection-tracker {
  // position: fixed;
  // bottom: 0px;
  // background: whitesmoke;
  background: #fff;
  display: flex;
  flex-direction: row;
  align-items: center;
  // min-height: 52px;
  width: 100%;
  transition: 0.2s all ease-in-out;
  padding: 10px 15px;
  z-index: 5;

  @media (min-width: $desktop) {
    justify-content: space-between;
  }

  @media (max-width: $desktop) {
    justify-content: flex-end;
  }

  a {
    transition: all 0.2s ease-in-out;
  }

  .selection-section {
    .incomplete {
      a {
        color: #bbb;
      }
    }
    @media (max-width: $desktop) {
      display: none
    }
    &:hover {
      a {
        color: #F16635;
      }
    }
    &.selected {
      .section-label {
        font-weight: bold;
        color: #aaa;
      }
      a {
        color: #F16635;
      }
    }
  }
  .section-divider {
    @media (max-width: $desktop) {
      display: none
    }
    margin: 0px 15px;
    color: #bbb;
  }
  .section-label {
    transition: all 0.2s ease-in-out;
    font-size: 0.7em;
    color: #bbb;
  }
  .project-size {
    margin: 0px 10px;
  }
  .product-sample {
    width: 35px;
    height: 35px;
    border-radius: 50%;
    overflow: hidden;
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: center;
    object-fit: contain;
    margin: 0px 10px;
    cursor: pointer;
  }
  .report-buttons {
    @media (max-width: $desktop) {
      display: none;
    }
  }
}

.is-orange {
  background: #F06733;
  color: #fff;
  &:hover, &:focus {
    color: #fff;
  }
}

.empty-message {
  background: whitesmoke;
  color: #bbb;
  padding: 2em 0.75em;
  margin: 1em 0em;
  border-radius: 10px;
}

.report {
  text-align: center;
  @media print {
    font-size: 0.75em;
    .product__image--cladding {
      width: 90px;
      min-height: 90px;
      height: auto;
      margin: 5px 5px;
    }
    .product__image {
      width: 100px;
      min-height: 100px;
      height: auto;
    }
  }
  &__header {
    font-weight: bold;
    font-size: 1.2em;
    color: #686868;
    margin-bottom: 1em;
  }
  &__subheader {
    font-size: 1em;
    color: #888888;
    margin-top: -0.5em;
    margin-bottom: 1em;
  }
  &__section {
    margin-bottom: 1.5em;
    padding-bottom: 1em;
    &:not(:first-child) {
      margin: 1.5em 0em;
    }
  }
  &__product {
    padding: 0em 1em;
    .product__image--cladding {
      width: 160px;
      min-height: 160px;
      height: auto;
      margin: 20px 20px;
    }
  }
}

.env-impact {
  &__graphics {
    width: 100%;
    font-size: 0.8em;
    @media (min-width: $tablet) {
      font-size: 1em;
    }
    @media(min-width:$desktop) {
      font-size: 1em;
      width: 80%;
    }
    @media(min-width:$widescreen) {
      font-size: 1em;
      width: 75%;
    }
  }
  &__wrapper {
    display: flex;
    flex-direction: row;
  }
  &__first {
    width: 35%;
    .env-impact__item {
      width: 60%;
    }
  }
  &__second {
    width: 65%;
    display: flex;
    flex-direction: row;
  }
  &__item {
    width: 30%;
  }
  &__spacer {
    width: 12%;
    &.large {
      width: 22%;
    }
  }
  &__number {
    font-size: 1em;
    font-weight: bold;
    color: #F16635;
    @media (min-width: $tablet) {
      font-size: 1.3em;
    }
    @media (min-width: $desktop) {
      font-size: 1.5em;
    }
  }
  &__footnote {
    margin-top: 1em;
    font-size: 0.9em;
  }
}

.modal-content {
  width: 1200px;
  .modal-card {
    width: 100%;
  }
  @media (max-width: $desktop) {
    width: 100%;
    height: 100vh;
    max-height: 100vh;
    .modal-card {
      margin-left: 0px;
      margin-right: 0px;
      max-height: 100vh;
    }
    .modal-card-head {
      border-radius: 0px;
    }
    .modal-card-foot {
      border-radius: 0px;
      display: flex;
      flex-direction: row;
      align-items: center;
      justify-content: center;
    }
  }
}

.text--capitalize {
  text-transform: capitalize;
}

.hide-smallscreen {
  @media (max-width: $desktop) {
    display: none !important;
  }
}
</style>